import { useEffect, useRef, useState } from "react";
import type { Pose } from "@soheil/core";
import { SoheilRig } from "./Soheil";
import { riveAvailable, loadRive } from "./rive";

/**
 * سهیلِ متحرک.
 *
 * انیمیشن‌ها Lottie هستند (tools/lottie). چون Rive فرمت بسته دارد و فقط با
 * ادیتور خودش ساخته می‌شود، اینجا برای هر ژست یک فایل مستقل داریم و گذارِ
 * بین ژست‌ها را با یک fade کوتاه انجام می‌دهیم.
 *
 * سه لایه، به ترتیب اولویت:
 *   ۱. **Rive** — اگر فایل طراح روی سرور باشد. یک بار بار می‌شود و بعد فقط
 *      عددِ ژست عوض می‌شود، پس گذارها نرم‌اند.
 *   ۲. **Lottie** — انیمیشن‌های فعلی، هر ژست یک فایل.
 *   ۳. **SVG** — رگ داخلی. همیشه هست و هیچ‌وقت غیب نمی‌شود.
 *
 * سه قاعده که رعایتشان مهم است:
 *   ۱. فقط دارایی ژست فعلی دانلود می‌شود — کاربر ایرانی روی دیتای موبایل است.
 *   ۲. هر خطایی (شبکه، مرورگر قدیمی، فایل خراب) یک پله پایین می‌آید، نه اینکه
 *      صفحه را خالی کند.
 *   ۳. اگر کاربر «کاهش حرکت» را روشن کرده باشد، هیچ‌کدام بار نمی‌شوند.
 */

type LottieApi = {
  loadAnimation: (o: Record<string, unknown>) => { destroy: () => void };
};

let lottiePromise: Promise<LottieApi | null> | null = null;
const cache = new Map<Pose, unknown>();

function loadLottie(): Promise<LottieApi | null> {
  if (!lottiePromise) {
    lottiePromise = import("lottie-web/build/player/lottie_light")
      .then((m) => (m.default ?? m) as unknown as LottieApi)
      .catch(() => null);
  }
  return lottiePromise;
}

async function loadPose(pose: Pose): Promise<unknown | null> {
  if (cache.has(pose)) return cache.get(pose);
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}soheil/${pose}.json`);
    if (!res.ok) return null;
    const data = await res.json();
    cache.set(pose, data);
    return data;
  } catch {
    return null;
  }
}

function prefersReducedMotion() {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function SoheilStage({ pose, className = "" }: { pose: Pose; className?: string }) {
  const host = useRef<HTMLDivElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const riveSet = useRef<((p: Pose) => void) | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rive, setRive] = useState(false);

  // ── لایهٔ اول: Rive ──
  useEffect(() => {
    if (prefersReducedMotion()) return;
    let dead = false;
    let inst: { cleanup?: () => void } | null = null;

    void (async () => {
      if (!(await riveAvailable())) return;
      if (dead || !canvas.current) return;
      try {
        const out = await loadRive(canvas.current, pose);
        if (dead) { out.instance.cleanup?.(); return; }
        inst = out.instance;
        riveSet.current = out.setPose;
        setRive(true);
      } catch {
        // فایل هست ولی باز نشد — می‌رویم سراغ Lottie
        setRive(false);
      }
    })();

    return () => {
      dead = true;
      riveSet.current = null;
      try { inst?.cleanup?.(); } catch { /* در حال بسته شدن */ }
    };
    // عمداً فقط یک بار: Rive یک بار بار می‌شود و بعد فقط ورودی‌اش عوض می‌شود
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // عوض شدن ژست در Rive یعنی عوض کردن یک عدد، نه بار کردن دوباره
  useEffect(() => {
    riveSet.current?.(pose);
  }, [pose]);

  // ── لایهٔ دوم: Lottie ──
  useEffect(() => {
    if (prefersReducedMotion() || rive) return;
    let anim: { destroy: () => void } | null = null;
    let dead = false;

    (async () => {
      const [lottie, data] = await Promise.all([loadLottie(), loadPose(pose)]);
      if (dead || !lottie || !data || !host.current) return;
      host.current.innerHTML = "";
      try {
        anim = lottie.loadAnimation({
          container: host.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          animationData: data,
          rendererSettings: { progressiveLoad: true },
        });
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    })();

    return () => {
      dead = true;
      setPlaying(false);
      try {
        anim?.destroy();
      } catch {
        /* مرورگر در حال بسته شدن — مهم نیست */
      }
    };
  }, [pose, rive]);

  return (
    <div className={`soheil-stage ${className}`}>
      {/* لایهٔ پشتی: همیشه هست. هرکدام از دو لایهٔ بالا که آمد، رویش می‌نشیند. */}
      <svg
        viewBox="0 0 800 800"
        data-pose={pose}
        aria-hidden="true"
        className={playing || rive ? "is-hidden" : ""}
      >
        <SoheilRig />
      </svg>
      <div className="soheil-player" ref={host} aria-hidden="true" hidden={rive} />
      <canvas className="soheil-canvas" ref={canvas} aria-hidden="true" hidden={!rive} />
    </div>
  );
}
